/**
 * Campaign Views - LocalStorage Persistence
 * 
 * Functions for loading, saving, and managing custom views in localStorage.
 */

import { relabelStockViewNames } from './constants';

const STORAGE_KEY_PREFIX = 'fokus-campaign-views-';

/**
 * Get the localStorage key for a campaign's views
 * @param {string} campaignId
 * @returns {string}
 */
function getStorageKey(campaignId) {
  return `${STORAGE_KEY_PREFIX}${campaignId}`;
}

/**
 * Load custom views for a campaign from localStorage
 * @param {string} campaignId
 * @returns {Object[]} Array of custom views
 */
export function loadCampaignViews(campaignId) {
  if (!campaignId) return [];
  
  try {
    const stored = localStorage.getItem(getStorageKey(campaignId));
    if (!stored) return [];
    
    const views = JSON.parse(stored);
    return Array.isArray(views) ? relabelStockViewNames(views) : [];
  } catch (err) {
    console.error('[campaignViews] Failed to load views:', err);
    return [];
  }
}

/**
 * Save custom views for a campaign to localStorage
 * @param {string} campaignId
 * @param {Object[]} views - Array of custom views
 */
export function saveCampaignViews(campaignId, views) {
  if (!campaignId) return;
  
  try {
    localStorage.setItem(getStorageKey(campaignId), JSON.stringify(views));
  } catch (err) {
    console.error('[campaignViews] Failed to save views:', err);
  }
}

/**
 * Add a new custom view for a campaign
 * @param {string} campaignId
 * @param {Object} view - View to add
 * @returns {Object[]} Updated views array
 */
export function addCampaignView(campaignId, view) {
  const views = loadCampaignViews(campaignId);
  const updatedViews = [...views, view];
  saveCampaignViews(campaignId, updatedViews);
  return updatedViews;
}

/**
 * Update a custom view for a campaign
 * @param {string} campaignId
 * @param {string} viewId
 * @param {Object} updates - Partial view updates
 * @returns {Object[]} Updated views array
 */
export function updateCampaignView(campaignId, viewId, updates) {
  const views = loadCampaignViews(campaignId);
  const updatedViews = views.map(v => 
    v.id === viewId 
      ? { ...v, ...updates, updatedAt: new Date().toISOString() }
      : v
  );
  saveCampaignViews(campaignId, updatedViews);
  return updatedViews;
}

/**
 * Delete a custom view for a campaign
 * @param {string} campaignId
 * @param {string} viewId
 * @returns {Object[]} Updated views array
 */
export function deleteCampaignView(campaignId, viewId) {
  const views = loadCampaignViews(campaignId);
  const updatedViews = views.filter(v => v.id !== viewId);
  saveCampaignViews(campaignId, updatedViews);
  return updatedViews;
}

/**
 * Reorder custom views for a campaign
 * @param {string} campaignId
 * @param {string[]} viewIds - New order of view IDs
 * @returns {Object[]} Updated views array
 */
export function reorderCampaignViews(campaignId, viewIds) {
  const views = loadCampaignViews(campaignId);
  const viewMap = new Map(views.map(v => [v.id, v]));
  const reorderedViews = viewIds
    .map(id => viewMap.get(id))
    .filter(Boolean);
  saveCampaignViews(campaignId, reorderedViews);
  return reorderedViews;
}
